import type { Rng } from '../../core/rng';

export interface Painter {
  readonly ctx: CanvasRenderingContext2D;
  readonly w: number;
  readonly h: number;
  readonly cx: number;
  readonly cy: number;
  readonly unit: number;
  readonly rng: Rng;
  readonly level: number;
  readonly font: string;
  readonly vanish: { readonly x: number; readonly y: number };
}

export interface Spot {
  readonly x: number;
  readonly y: number;
}

const TAU = Math.PI * 2;

export function rgba(color: number, alpha = 1): string {
  return `rgba(${String((color >> 16) & 255)},${String((color >> 8) & 255)},${String(color & 255)},${String(alpha)})`;
}

export function calmAt(p: Painter, x: number, y: number, keep: number): boolean {
  const dx = (x - p.cx) / (keep * p.unit * 1.08);
  const dy = (y - p.cy + 0.08 * p.unit) / (keep * p.unit);
  const vx = x - p.vanish.x;
  const vy = y - p.vanish.y;
  return dx * dx + dy * dy <= 1 || Math.hypot(vx, vy) < 0.34 * p.unit;
}

export function edgeSpot(p: Painter, keep: number, margin = 0): Spot {
  for (let i = 0; i < 60; i++) {
    const x = margin + p.rng.float() * Math.max(1, p.w - 2 * margin);
    const y = margin + p.rng.float() * Math.max(1, p.h - 2 * margin);
    if (!calmAt(p, x, y, keep)) return { x, y };
  }
  return { x: p.rng.chance(0.5) ? margin : p.w - margin, y: p.rng.chance(0.5) ? margin : p.h - margin };
}

export function spots(p: Painter, count: number, keep: number, margin: number, spacing: number): Spot[] {
  const out: Spot[] = [];
  for (let tries = 0; out.length < count && tries < count * 30; tries++) {
    const s = edgeSpot(p, keep, margin);
    if (out.every((o) => Math.hypot(o.x - s.x, o.y - s.y) >= spacing)) out.push(s);
  }
  return out;
}

export function sketchCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rng: Rng, jitter = 0.04): void {
  const start = rng.float() * TAU;
  const turn = TAU + rng.range(0.15, 0.45);
  const steps = Math.max(18, Math.round(r / 2));
  const p1 = rng.float() * TAU;
  const p2 = rng.float() * TAU;
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = start + t * turn;
    const k = 1 + jitter * (0.6 * Math.sin(2 * a + p1) + 0.4 * Math.sin(3 * a + p2)) + jitter * 0.8 * t;
    const px = x + Math.cos(a) * r * k;
    const py = y + Math.sin(a) * r * k;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();
}

export function sketchLine(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  rng: Rng,
  bow = 0.04,
): void {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const nx = -(y1 - y0) / (len || 1);
  const ny = (x1 - x0) / (len || 1);
  const b = rng.range(-bow, bow) * len;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.quadraticCurveTo((x0 + x1) / 2 + nx * b, (y0 + y1) / 2 + ny * b, x1, y1);
  ctx.stroke();
}

export function starPath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, inner: number, points: number, rot: number): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const a = rot - Math.PI / 2 + (i * Math.PI) / points;
    const rr = i % 2 === 0 ? r : r * inner;
    const px = x + Math.cos(a) * rr;
    const py = y + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

export function heartPath(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y + s * 0.35);
  ctx.bezierCurveTo(x - s * 1.1, y - s * 0.35, x - s * 0.45, y - s * 1.05, x, y - s * 0.45);
  ctx.bezierCurveTo(x + s * 0.45, y - s * 1.05, x + s * 1.1, y - s * 0.35, x, y + s * 0.35);
  ctx.closePath();
}

export function hatchCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, spacing: number, angle: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.clip();
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  ctx.beginPath();
  for (let d = -r; d <= r; d += spacing) {
    const bx = x - uy * d;
    const by = y + ux * d;
    ctx.moveTo(bx - ux * r, by - uy * r);
    ctx.lineTo(bx + ux * r, by + uy * r);
  }
  ctx.stroke();
  ctx.restore();
}

export function glowDot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: number, alpha: number): void {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, rgba(color, alpha));
  g.addColorStop(0.35, rgba(color, alpha * 0.45));
  g.addColorStop(1, rgba(color, 0));
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

export function grain(p: Painter, alpha: number, light: boolean): void {
  const tile = document.createElement('canvas');
  tile.width = 96;
  tile.height = 96;
  const t = tile.getContext('2d');
  if (t === null) return;
  const img = t.createImageData(96, 96);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = light ? 255 : 30;
    img.data[i] = v;
    img.data[i + 1] = v;
    img.data[i + 2] = v;
    img.data[i + 3] = Math.floor(p.rng.float() ** 3 * 255 * alpha);
  }
  t.putImageData(img, 0, 0);
  const pattern = p.ctx.createPattern(tile, 'repeat');
  if (pattern === null) return;
  p.ctx.fillStyle = pattern;
  p.ctx.fillRect(0, 0, p.w, p.h);
}

export function calmCenter(p: Painter, color: number, alpha: number, reach: number): void {
  const r = p.unit * reach;
  const y = p.cy - 0.08 * p.unit;
  const g = p.ctx.createRadialGradient(p.cx, y, r * 0.15, p.cx, y, r);
  g.addColorStop(0, rgba(color, alpha));
  g.addColorStop(0.6, rgba(color, alpha * 0.7));
  g.addColorStop(1, rgba(color, 0));
  p.ctx.fillStyle = g;
  p.ctx.fillRect(0, 0, p.w, p.h);
}

export function vignette(p: Painter, color: number, alpha: number): void {
  const r = Math.hypot(p.w, p.h) / 2;
  const g = p.ctx.createRadialGradient(p.w / 2, p.h / 2, r * 0.55, p.w / 2, p.h / 2, r);
  g.addColorStop(0, rgba(color, 0));
  g.addColorStop(1, rgba(color, alpha));
  p.ctx.fillStyle = g;
  p.ctx.fillRect(0, 0, p.w, p.h);
}

export function catenary(x0: number, y0: number, x1: number, y1: number, sagBy: number, t: number): Spot {
  const x = x0 + (x1 - x0) * t;
  const y = y0 + (y1 - y0) * t + 4 * sagBy * t * (1 - t);
  return { x, y };
}
